import { useCallback, useEffect, useRef, useState } from "react";
import { Copy, Film, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg, mediaSrc } from "../../lib/api";

const LIMITS = { image: [["image/jpeg", "image/png", "image/webp", "image/gif"], 5], video: [["video/mp4", "video/webm"], 50] };

function validateFile(file) {
  const kind = file.type.startsWith("video/") ? "video" : "image";
  const [types, mb] = LIMITS[kind];
  if (!types.includes(file.type)) return "Unsupported type. Allowed: JPG, PNG, WEBP, GIF, MP4, WEBM.";
  if (file.size > mb * 1024 * 1024) return `File too large. Max ${mb} MB for ${kind}s.`;
  return null;
}

export default function MediaLibrary() {
  const { canEdit } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = useCallback(() => api.get("/admin/media").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateFile(file);
    if (err) { toast.error(err); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/admin/media/upload", fd); toast.success("Uploaded"); load(); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  const patch = (m, body) => api.patch(`/admin/media/${m.id}`, body).then(load).catch((x) => toast.error(errMsg(x)));
  const remove = async (m) => { if (window.confirm("Remove this media item?")) { await api.delete(`/admin/media/${m.id}`); load(); } };
  const copy = (m) => { navigator.clipboard?.writeText(m.url); toast.success("URL copied"); };

  return (
    <section data-testid="media-library">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#4A5568]">Images up to 5 MB (JPG, PNG, WEBP, GIF). Videos up to 50 MB (MP4, WEBM).</p>
        {canEdit && <>
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" className="sr-only" onChange={upload} id="media-upload" data-testid="media-upload-input" />
          <label htmlFor="media-upload" className="btn-primary btn-sm cursor-pointer" data-testid="media-upload-btn"><Upload size={16} aria-hidden="true" /> {busy ? "Uploading…" : "Upload"}</label>
        </>}
      </div>
      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((m) => (
          <li key={m.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`media-item-${m.id}`}>
            <div className="relative">
              {m.kind === "image" ? <img src={mediaSrc(m.url)} alt={m.alt || ""} className="aspect-video w-full rounded object-cover" />
                : <div className="flex aspect-video items-center justify-center rounded bg-slate-100"><Film aria-hidden="true" /></div>}
              {m.is_temporary && <span className="absolute left-2 top-2 rounded bg-gold px-2 py-0.5 text-[10px] font-bold text-navy" data-testid="media-temporary-badge">TEMPORARY</span>}
              {m.client_supplied && <span className="absolute left-2 top-2 rounded bg-navy px-2 py-0.5 text-[10px] font-bold text-white" title={m.source} data-testid="media-client-supplied-badge">CLIENT SUPPLIED</span>}
            </div>
            <label className="mt-2 block text-xs font-semibold text-slate-600" htmlFor={`label-${m.id}`}>Label</label>
            <input id={`label-${m.id}`} className="field-input py-1.5 text-sm" defaultValue={m.label || m.filename} disabled={!canEdit} onBlur={(e) => e.target.value !== m.label && patch(m, { label: e.target.value })} />
            <label className="mt-2 block text-xs font-semibold text-slate-600" htmlFor={`alt-${m.id}`}>Alt text</label>
            <input id={`alt-${m.id}`} className="field-input py-1.5 text-sm" defaultValue={m.alt} disabled={!canEdit} onBlur={(e) => e.target.value !== m.alt && patch(m, { alt: e.target.value })} />
            <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
              <span>{m.kind}{m.size ? ` · ${(m.size / 1024).toFixed(0)} KB` : ""}{m.source ? " · stock" : ""}</span>
              <span className="flex gap-2">
                <button type="button" onClick={() => copy(m)} aria-label="Copy URL" data-testid={`media-copy-${m.id}`}><Copy size={15} /></button>
                {canEdit && <button type="button" onClick={() => remove(m)} aria-label="Remove" className="text-red-700" data-testid={`media-delete-${m.id}`}><Trash2 size={15} /></button>}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
